import { Link } from '@tanstack/react-router';
import { ArrowLeft, RotateCcw, ShieldCheck, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { AUTO_REJECT_MIN_APPLICATIONS, AUTO_REJECT_WARNING_PERCENT, type AutoRejectedRow } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { SkeletonLines, Skeleton } from '../../../components/Skeleton';
import { ForbiddenState, NotFoundState, ServerErrorState, SystemState } from '../../../components/system-states/SystemState';
import { errorMessage } from '../../../lib/api-client';
import { formatDate, orEmpty } from '../../../lib/format';
import { useToasts } from '../../../stores/toasts';
import { useAutoRejected, useRestoreApplication } from '../api/sorting-api';
import styles from './AutoRejectedPage.module.css';

/**
 * SCR-04 / F8.12 — everyone a knockout question turned away, each restorable in one
 * click (§L8: "auto-rules can be wrong; a human can rescue"). The rate at the top is
 * the one N-18 watches.
 */
export function AutoRejectedPage({ jobId }: { readonly jobId: string }): ReactNode {
  const list = useAutoRejected(jobId);
  const restore = useRestoreApplication(jobId);
  const toast = useToasts((state) => state.show);

  if (list.isPending) {
    return (
      <div className={styles.page}>
        <Skeleton shape="title" width="40%" />
        <SkeletonLines count={6} />
      </div>
    );
  }
  if (list.error) {
    if (list.error.status === 403) return <ForbiddenState resource="this job's applications" contact="Your admin" />;
    if (list.error.status === 404) return <NotFoundState />;
    return <ServerErrorState onRetry={() => void list.refetch()} />;
  }
  const data = list.data;

  async function rescue(row: AutoRejectedRow): Promise<void> {
    try {
      await restore.mutateAsync({ applicationId: row.applicationId, version: row.version });
      toast({ tone: 'success', message: `${row.candidateName} restored`, detail: 'They are back in the sorting queue.' });
    } catch (cause) {
      toast({ tone: 'danger', message: `${row.candidateName} wasn't restored`, detail: errorMessage(cause) });
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Link to="/jobs/$jobId" params={{ jobId }} className={styles.back}>
          <ArrowLeft size={14} aria-hidden />
          {data.job.reference} · {data.job.title}
        </Link>
        <h1 className={styles.title}>Turned away automatically</h1>
        <p className={styles.lead}>
          People a screening question rejected. The rules are sometimes wrong — restore anyone who deserves a look and they join the sorting queue.
        </p>
      </header>

      <Rate percent={data.autoRejectedPercent} total={data.applicationCount} count={data.rows.length} isWarning={data.isWarning} />

      {data.rows.length === 0 ? (
        <SystemState icon={ShieldCheck} title="Nobody is waiting here" body="No one on this job has been turned away by a screening question." isInset />
      ) : (
        <ul className={styles.rows}>
          {data.rows.map((row) => (
            <li key={row.applicationId} className={styles.row}>
              <div className={styles.who}>
                <Link to="/candidates/$candidateId" params={{ candidateId: row.candidateId }} className={styles.name}>
                  {row.candidateName}
                </Link>
                <span className={styles.meta}>
                  {row.reference} · applied {formatDate(row.appliedAt)} · match {orEmpty(row.matchScore)}
                </span>
              </div>
              <ul className={styles.failed} aria-label="Questions not passed">
                {row.failed.length === 0 ? (
                  <li className={styles.meta}>—</li>
                ) : (
                  row.failed.map((answer) => (
                    <li key={answer.question}>
                      <span className={styles.question}>{answer.question}</span> <span className={styles.meta}>answered {orEmpty(answer.answer)}</span>
                    </li>
                  ))
                )}
              </ul>
              {data.canRestore && (
                <Button size="sm" variant="outline" icon={RotateCcw} onClick={() => void rescue(row)}>
                  Restore
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Rate({ percent, total, count, isWarning }: { readonly percent: number | null; readonly total: number; readonly count: number; readonly isWarning: boolean }): ReactNode {
  if (percent === null) {
    return (
      <p className={styles.rate}>
        {count} turned away of {total} applicants. A rate shows once there are {AUTO_REJECT_MIN_APPLICATIONS}.
      </p>
    );
  }
  return (
    <p className={isWarning ? styles.rateWarning : styles.rate} role={isWarning ? 'alert' : undefined}>
      {isWarning && <TriangleAlert size={16} aria-hidden />}
      <span>
        <strong>{percent}%</strong> of {total} applicants turned away automatically.
        {isWarning ? ` Above ${AUTO_REJECT_WARNING_PERCENT}% usually means a screening question is set too strictly — check the job's application form.` : ''}
      </span>
    </p>
  );
}
